/**
 * Standalone WebSocket Service
 * Connects to standalone_session_service.py for receiving transcripts
 * and alerts when using the desktop app for audio capture
 *
 * Port: 6345
 */

import { config } from '../config';

export interface TranscriptData {
  text: string;
  speaker: 'salesperson' | 'customer';
  timestamp: string;
}

export interface AlertData {
  type: string;
  data: any;
}

type TranscriptCallback = (data: TranscriptData) => void;
type AlertCallback = (type: string, data: any) => void;
type ConnectionCallback = (connected: boolean) => void;

class StandaloneWebSocketService {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;

  private transcriptCallbacks: TranscriptCallback[] = [];
  private alertCallbacks: AlertCallback[] = [];
  private connectionCallbacks: ConnectionCallback[] = [];

  /**
   * Connect to standalone session service
   */
  async connect(sessionId: string, token: string): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        this.sessionId = sessionId;

        const wsUrl = config.server.standaloneWsUrl || 'ws://localhost:6345';
        const url = `${wsUrl}/ws/frontend?session_id=${sessionId}&token=${token}`;

        console.log(`[StandaloneWS] Connecting to ${url}`);

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('[StandaloneWS] Connected to standalone session service');
          this.reconnectAttempts = 0;
          this.notifyConnectionChange(true);
          resolve(true);
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log(`[StandaloneWS] Connection closed: ${event.code} ${event.reason}`);
          this.notifyConnectionChange(false);
          this.attemptReconnect(token);
        };

        this.ws.onerror = (error) => {
          console.error('[StandaloneWS] WebSocket error:', error);
          resolve(false);
        };

        // Timeout for initial connection
        setTimeout(() => {
          if (this.ws?.readyState !== WebSocket.OPEN) {
            console.warn('[StandaloneWS] Connection timeout');
            resolve(false);
          }
        }, 10000);

      } catch (error) {
        console.error('[StandaloneWS] Failed to connect:', error);
        resolve(false);
      }
    });
  }

  /**
   * Disconnect from standalone session service
   */
  disconnect(): void {
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    this.sessionId = null;
    this.notifyConnectionChange(false);
    console.log('[StandaloneWS] Disconnected');
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Register callback for transcript events
   */
  onTranscript(callback: TranscriptCallback): () => void {
    this.transcriptCallbacks.push(callback);
    return () => {
      this.transcriptCallbacks = this.transcriptCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Register callback for alert events
   */
  onAlert(callback: AlertCallback): () => void {
    this.alertCallbacks.push(callback);
    return () => {
      this.alertCallbacks = this.alertCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Register callback for connection status changes
   */
  onConnectionChange(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.push(callback);
    return () => {
      this.connectionCallbacks = this.connectionCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);
      const messageType = message.type;

      switch (messageType) {
        case 'connection_established':
          console.log('[StandaloneWS] Connection confirmed for session:', message.session_id);
          break;

        case 'transcript':
          const transcriptData: TranscriptData = {
            text: message.data?.text || '',
            speaker: message.data?.speaker || 'unknown',
            timestamp: message.data?.timestamp || new Date().toISOString()
          };
          this.transcriptCallbacks.forEach(cb => cb(transcriptData));
          break;

        case 'question_detected':
        case 'landmine_alert':
        case 'competitor_alert':
        case 'speech_rate_alert':
        case 'speech_rate_update':
          this.alertCallbacks.forEach(cb => cb(messageType, message.data || {}));
          break;

        case 'session_ended':
          console.log('[StandaloneWS] Session ended');
          this.disconnect();
          break;

        default:
          console.log('[StandaloneWS] Unknown message type:', messageType);
      }
    } catch (error) {
      console.error('[StandaloneWS] Error parsing message:', error);
    }
  }

  /**
   * Attempt to reconnect after connection loss
   */
  private async attemptReconnect(token: string): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[StandaloneWS] Max reconnection attempts reached');
      return;
    }

    if (!this.sessionId) {
      console.log('[StandaloneWS] No session ID for reconnection');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);

    console.log(`[StandaloneWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    await new Promise(resolve => setTimeout(resolve, delay));

    if (this.sessionId) {
      await this.connect(this.sessionId, token);
    }
  }

  /**
   * Notify all connection callbacks
   */
  private notifyConnectionChange(connected: boolean): void {
    this.connectionCallbacks.forEach(cb => cb(connected));
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }
}

// Export singleton instance
export const standaloneWebSocketService = new StandaloneWebSocketService();
